import { UseQueryResult, useQuery } from '@tanstack/react-query'

import Api from 'services/Api'
import { hashTable } from 'services/Utilities'

import { CACHE_TIME, RESSOURCES_ENDPOINTS, STALE_TIME } from 'Api/constants'
import { User } from 'Api/types'
import { queryClient } from 'services/QueryClient'

type Options = {
  archived?: boolean
  page?: number
  limit?: number
  filter?: Record<string, unknown>
  sortBy?: string
}

type UserResponse = {
  users: User[]
  count: number
  invalidCount: number
  billable: number
}

const usersQuery = async (options?: Options) => {
  const response: { users: User[]; count: number; invalidCount: number; billable: number } = await Api.get(RESSOURCES_ENDPOINTS.users, {
    params: { ...options?.filter, limit: options?.limit, page: options?.page, sort: options?.sortBy || 'firstname', showArchived: options?.archived }
  })
  return usersTransformer(response)
}

const usersTransformer = ({ users, count, invalidCount, billable }: UserResponse) =>
  ({
    users,
    usersIds: users?.map(({ _id }) => _id),
    usersById: users?.length ? hashTable(users) : undefined,
    count: count ?? 0,
    invalidCount: invalidCount ?? 0,
    billable
  } as const)

const getQueryConfig = (options?: Options) =>
  ({
    queryKey: ['users', { ...options }],
    queryFn: () => usersQuery(options),
    cacheTime: CACHE_TIME,
    staleTime: STALE_TIME
  } as const)

type Select<T> = (data: UsersData) => T

const useUsers = <T = UsersData>(select?: Select<T>, options?: Options) => {
  const config = getQueryConfig(options)
  return useQuery({ ...config, select })
}

const useUserById = (id: string): UseQueryResult<User | undefined> => {
  return useUsers(data => data.usersById?.[id])
}

const prefetchUsers = async () => queryClient.prefetchQuery({ ...getQueryConfig() })
const invalidateUsers = async (options?: Options) => queryClient.invalidateQueries(getQueryConfig(options).queryKey)
const getUsersQueryCache = (options?: Options) => queryClient.getQueryData<UsersData>(getQueryConfig(options).queryKey)

type UsersData = ReturnType<typeof usersTransformer>

export type { UsersData }
export { useUsers, prefetchUsers, invalidateUsers, useUserById, getUsersQueryCache }
